import { createStripeCheckoutSession } from '../api/axios';
import { resolveStripeCancelPath, STRIPE_URLS } from '../constants/urls';
import posthog from 'posthog-js';

export type CheckoutPlan = 'pro' | 'ultra';

export type InitiateCheckoutOptions = {
  /** Allowlisted path (e.g. from `?cancel=`) for Stripe `cancel_url`; unsafe values fall back. */
  cancelPath?: string | null;
  source?: string;
  /** Explicit Stripe price — required when a product has multiple active prices. */
  priceId?: string;
};

const PLAN_CATALOG: Record<
  CheckoutPlan,
  { productId: string; priceId: string }
> = {
  pro: {
    productId: process.env.REACT_APP_PRODUCT_ID || '',
    priceId: process.env.REACT_APP_PRO_PRICE_ID || '',
  },
  ultra: {
    productId:
      process.env.REACT_APP_ULTRA_PRODUCT_ID ||
      process.env.REACT_APP_PRODUCT_ID ||
      '',
    priceId: process.env.REACT_APP_ULTRA_PRICE_ID || '',
  },
};

export function resolveCheckoutIds(plan: CheckoutPlan = 'pro') {
  return PLAN_CATALOG[plan];
}

export const initiateCheckout = async (
  planOrProductId: CheckoutPlan | string = 'pro',
  options?: InitiateCheckoutOptions
) => {
  const source = options?.source ?? 'pricing';
  const origin = process.env.REACT_APP_ORIGIN || window.location.origin;
  const cancelPath = resolveStripeCancelPath(options?.cancelPath);
  const cancelUrl = `${origin}${cancelPath}`;

  const isPlan = planOrProductId === 'pro' || planOrProductId === 'ultra';
  const plan: CheckoutPlan | undefined = isPlan ? planOrProductId : undefined;
  const { productId, priceId } = isPlan
    ? resolveCheckoutIds(planOrProductId)
    : {
        productId: planOrProductId || process.env.REACT_APP_PRODUCT_ID || '',
        priceId:
          options?.priceId || process.env.REACT_APP_PRO_PRICE_ID || '',
      };

  const resolvedPriceId = options?.priceId || priceId;

  try {
    const checkoutSession = await createStripeCheckoutSession(
      productId,
      STRIPE_URLS.SUCCESS_URL,
      cancelUrl,
      1,
      resolvedPriceId || undefined,
      plan
    );

    posthog.capture('checkout_session_created', {
      product_id: productId,
      price_id: resolvedPriceId || undefined,
      plan,
      source,
    });

    // Redirect to Stripe checkout
    if (checkoutSession.url) {
      window.location.href = checkoutSession.url;
    }

    return checkoutSession;
  } catch (error) {
    posthog.capture('checkout_failed', {
      product_id: productId,
      price_id: resolvedPriceId || undefined,
      plan,
      source,
      error: error instanceof Error ? error.message : String(error),
    });
    console.error('Failed to create checkout session:', error);
    throw error;
  }
};

// Example function with custom URLs
export const initiateCheckoutWithCustomUrls = async (
  productId: string,
  successUrl: string,
  cancelUrl: string,
  quantity: number = 1,
  source: string = 'extension',
  priceId?: string
) => {
  try {
    const checkoutSession = await createStripeCheckoutSession(
      productId,
      successUrl,
      cancelUrl,
      quantity,
      priceId
    );

    posthog.capture('checkout_session_created', {
      product_id: productId,
      price_id: priceId || undefined,
      source,
      quantity,
    });

    // Redirect to Stripe checkout
    if (checkoutSession.url) {
      window.location.href = checkoutSession.url;
    }

    return checkoutSession;
  } catch (error) {
    posthog.capture('checkout_failed', {
      product_id: productId,
      price_id: priceId || undefined,
      source,
      error: error instanceof Error ? error.message : String(error),
    });
    console.error('Failed to create checkout session:', error);
    throw error;
  }
};
